import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AGE_GROUPS, AgeGroupValue } from '../utils/constants';

interface AgeGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedAgeGroup: string;
    onSelect: (ageGroup: AgeGroupValue) => void;
    disabled?: boolean;
}

export default function AgeGroupModal({
    isOpen,
    onClose,
    selectedAgeGroup,
    onSelect,
    disabled = false
}: AgeGroupModalProps) {
    const { t } = useTranslation();

    // Close modal on Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSelect = (ageGroup: AgeGroupValue) => {
        if (!disabled) {
            onSelect(ageGroup);
            onClose();
        }
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div 
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="age-group-modal-title"
        >
            {/* Modal Content - Dropdown Style */}
            <div 
                className="relative bg-black border border-[var(--color-light-gray)] rounded-lg shadow-lg w-full max-w-md mx-8 max-h-[50vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sr-only" id="age-group-modal-title">
                    {t('registration.selectAgeGroup')}
                </div>
                
                {AGE_GROUPS.map((ageGroup) => (
                    <div
                        key={ageGroup.value}
                        className={`px-4 py-2 text-center cursor-pointer hover:bg-gray-800 text-white ${
                            selectedAgeGroup === ageGroup.value ? 'bg-blue-900' : ''
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => handleSelect(ageGroup.value)}
                    >
                        {ageGroup.label}
                    </div>
                ))}
            </div>
        </div>
    );
} 